import { assert } from '../utils/assert.ts';
import { checkWebGPUSupport } from '../utils/browserChecks.ts';
import { log } from '../utils/logger.ts';
import { initWebGPU } from '../webgpu/initWebGPU.ts';
import { createCanvasContext } from '../webgpu/createContext.ts';
import { createUniforms } from '../webgpu/createUniforms.ts';
import { createFullscreenPipeline } from '../webgpu/createFullscreenPipeline.ts';
import { createRenderer } from '../webgpu/renderer.ts';
import { setupResize } from '../webgpu/resizeCanvas.ts';
import { setupGPUErrorHandling } from '../webgpu/gpuErrors.ts';
import { createTimeState } from '../state/timeState.ts';

export async function bootstrapApp(): Promise<void> {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
  assert(canvas, 'Canvas element not found');

  checkWebGPUSupport();

  const { adapter, device, format } = await initWebGPU();
  log.info('GPU device acquired:', adapter.info);

  setupGPUErrorHandling(device);

  const context = createCanvasContext(canvas, device, format);
  const uniforms = createUniforms(device);
  const { pipeline, bindGroup } = createFullscreenPipeline(
    device, format, uniforms.bindGroupLayout, uniforms.buffer,
  );

  const renderer = createRenderer({
    device,
    context,
    pipeline,
    bindGroup,
    uniformBuffer: uniforms.buffer,
  });

  const resize = setupResize(canvas, device);
  const time = createTimeState();

  const loop = (nowMs: number) => {
    time.update(nowMs);
    resize.updateIfNeeded();
    renderer.render(time.elapsed, canvas.width, canvas.height, time.frame);
    requestAnimationFrame(loop);
  };

  requestAnimationFrame(loop);
  log.info('Render loop started');
}
